import { ENTITY_URL } from '../../env';

type EntityLinkEvent = MouseEvent | React.MouseEvent<HTMLElement>;

// `userid` is already the encrypted id as returned by the customer-map backend API.
export const handleEntityLinkClick = async (event: EntityLinkEvent, userid: string) => {
  if ('preventDefault' in event) {
    event.preventDefault();
  }
  if ('stopPropagation' in event) {
    event.stopPropagation();
  }

  const targetElement =
    (event.currentTarget as HTMLElement | null) || (event.target as HTMLElement | null) || null;

  if (targetElement) {
    targetElement.style.pointerEvents = 'none';
  }

  try {
    window.open(`${ENTITY_URL}/clients/${userid}?tab=summary`, '_blank');
  } finally {
    if (targetElement) {
      targetElement.style.pointerEvents = 'auto';
    }
  }
};

// Make the function available globally for onclick handlers
(window as any).handleEntityLinkClick = handleEntityLinkClick;

export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  if (!Number.isFinite(num)) {
    return '0';
  }
  return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

export const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toFixed(2)}`;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const maskEmail = (value?: string | null) => {
  if (!value) return '';
  const trimmed = value.trim();
  const atIndex = trimmed.indexOf('@');
  if (atIndex <= 0 || atIndex === trimmed.length - 1) {
    return trimmed;
  }
  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  return `${local[0]}*****@${domain}`;
};

export const maskPhone = (value?: string | null) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return value.trim();
  return `***-***-${digits.slice(-4)}`;
};

const formatMaskedHtml = (value: string, masked: string) => {
  if (!value) return '';
  if (value === masked) return escapeHtml(value);
  return `<span title="${escapeHtml(value)}">${escapeHtml(masked)}</span>`;
};

export const formatExactNumber = (value: number): string => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return numeric.toLocaleString('en-US');
};

export const formatExactCurrency = (value: number): string => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
};

// Normalized row used for table rendering
export interface PopupTableRow {
  type: 'client' | 'vendor' | 'prospect' | 'warehouse' | 'product';
  name: string;
  userid?: string;
  location_id?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  staff?: string;
  orders?: number;
  products?: number;
  quotation_count?: number;
  enquiry_count?: number;
  open_so?: string;
  target?: string | number;
  target_year?: string | number;
  target_achievement?: string | number;
  total_amount?: string | number; // may be number or formatted string from API
  revenue_share?: string | number;
  invoice_created_date?: string; // ISO date string for last invoice
  email?: string;
  phone?: string;
  website_validation_notes?: string;
  website_found_address?: string;
  website_found_city?: string;
  website_found_state?: string;
  website_found_country?: string;
}

type PopupTableColumnKey =
  | 'type'
  | 'name'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'
  | 'country'
  | 'staff'
  | 'orders'
  | 'products'
  | 'quotation_count'
  | 'enquiry_count'
  | 'target'
  | 'target_year'
  | 'target_achievement'
  | 'total_amount'
  | 'revenue_share'
  | 'email'
  | 'phone'
  | 'action';

type PopupTableColumnVisibility = Partial<Record<PopupTableColumnKey, boolean>>;

// Build a table (HTML string) with search + pagination handled on the frontend
export const buildPopupTableContent = (
  allRows: PopupTableRow[],
  opts: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortKey?: 'name' | 'city' | 'staff' | 'orders' | 'products' | 'quotation_count' | 'enquiry_count' | 'revenue_share' | 'target' | 'target_year' | 'target_achievement' | 'total_amount';
    sortDir?: 'asc' | 'desc';
    columnVisibility?: PopupTableColumnVisibility;
    showColumnToggle?: boolean;
  }
): string => {
  const page = Math.max(1, opts.page || 1);
  const pageSize = Math.max(1, opts.pageSize || 10);
  const search = (opts.search || '').toLowerCase();
  const sortKey = opts.sortKey;
  const sortDir = opts.sortDir || 'asc';
  const showColumnToggle = opts.showColumnToggle !== false;

  type ColKey = PopupTableColumnKey;

  const matches = (row: PopupTableRow) => {
    if (!search) return true;
    const parts = [
      row.name,
      row.city,
      row.state,
      row.country,
      row.staff,
      row.email,
      row.phone,
    ]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase());
    return parts.some((p) => p.includes(search));
  };

  const filtered = allRows.filter(matches);

  // Sorting
  const cmp = (a: any, b: any) => (a < b ? -1 : a > b ? 1 : 0);
  const parseAmount = (v?: string | number) => {
    if (v === undefined || v === null || v === '') return 0;
    if (typeof v === 'number') return v;
    const s = v.trim();
    if (/m$/i.test(s)) return parseFloat(s) * 1_000_000;
    if (/k$/i.test(s)) return parseFloat(s) * 1_000;
    const num = parseFloat(s.replace(/[^0-9.-]+/g, ''));
    return Number.isFinite(num) ? num : 0;
  };
  const parsePercent = (v?: string | number) => {
    if (v === undefined || v === null || v === '') return 0;
    if (typeof v === 'number') return v;
    const cleaned = String(v).replace(/%/g, '').trim();
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const totalRevenueAmount = filtered.reduce((sum, row) => {
    if (row.type !== 'client') return sum;
    return sum + parseAmount(row.total_amount);
  }, 0);

  const getSortValue = (row: PopupTableRow) => {
    switch (sortKey) {
      case 'name': return (row.name || '').toLowerCase();
      case 'city': return (row.city || '').toLowerCase();
      case 'staff': return (row.staff || '').toLowerCase();
      case 'orders': return row.orders ?? -Infinity;
      case 'products': return row.products ?? -Infinity;
      case 'quotation_count': return row.quotation_count ?? -Infinity;
      case 'enquiry_count': return row.enquiry_count ?? -Infinity;
      case 'target': return parseAmount(row.target);
      case 'target_year': {
        const value = row.target_year;
        if (value === undefined || value === null || value === '') return -Infinity;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
        const parsed = parseFloat(String(value).replace(/[^0-9.-]+/g, ''));
        return Number.isFinite(parsed) ? parsed : -Infinity;
      }
      case 'target_achievement': return parsePercent(row.target_achievement);
      case 'total_amount': return parseAmount(row.total_amount);
      case 'revenue_share':
        if (row.type !== 'client' || totalRevenueAmount <= 0) return 0;
        return parseAmount(row.total_amount) / totalRevenueAmount;
      default: return 0;
    }
  };

  if (sortKey) {
    filtered.sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      const order = cmp(va, vb);
      return sortDir === 'asc' ? order : -order;
    });
  }
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const formatPercentCell = (display: string, numeric?: number | null) => {
    if (!display) return '';
    if (numeric === undefined || numeric === null || !Number.isFinite(numeric)) {
      return display;
    }
    return `<span title="${numeric.toFixed(2)}%">${display}</span>`;
  };

  const toAmount = (v?: string | number) => {
    if (v === undefined || v === null || v === '') return '';
    const hasDigits = typeof v === 'number' || (typeof v === 'string' && /\d/.test(v));
    const display = (() => {
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return '';
        if (/m$/i.test(trimmed) || /k$/i.test(trimmed)) {
          return trimmed.toUpperCase();
        }
        return trimmed;
      }
      return formatCurrency(Number(v));
    })();
    if (!display) return '';
    if (!hasDigits) {
      return display;
    }
    const numeric = parseAmount(v);
    return `<span title="${formatExactCurrency(numeric)}">${display}</span>`;
  };
  const toPercent = (v?: string | number) => {
    if (v === undefined || v === null || v === '') return '';
    const trimmed = typeof v === 'string' ? v.trim() : '';
    const hasDigits = typeof v === 'number' || (typeof v === 'string' && /\d/.test(trimmed));
    if (!hasDigits) {
      return trimmed;
    }
    const numeric = parsePercent(v);
    let display: string;
    if (typeof v === 'number') {
      const digits = Number.isInteger(v) ? 0 : 1;
      display = `${v.toFixed(digits)}%`;
    } else {
      if (!trimmed) return '';
      if (Number.isFinite(numeric)) {
        const digits = Number.isInteger(numeric) ? 0 : 1;
        display = `${numeric.toFixed(digits)}%`;
      } else {
        display = trimmed;
      }
    }
    return formatPercentCell(display, numeric);
  };

  // Serialize dataset for event handlers (kept minimal fields)
  const encodedDataset = encodeURIComponent(JSON.stringify(allRows));

  // Build dynamic columns based on row types present
  const presentTypes = new Set(filtered.map((r) => r.type));
  const hasProductRows = presentTypes.has('product');
  const isProspectOnly = presentTypes.size === 1 && presentTypes.has('prospect');

  const baseColumnDefs: { key: ColKey; label: string; sortable: boolean }[] = [
    { key: 'type', label: 'Type', sortable: false },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'address', label: 'Address', sortable: false },
    { key: 'city', label: 'City', sortable: true },
    { key: 'state', label: 'State', sortable: false },
    { key: 'zip', label: 'Zip', sortable: false },
    { key: 'country', label: 'Country', sortable: false },
    { key: 'staff', label: 'Sales Rep', sortable: true },
    { key: 'orders', label: hasProductRows ? 'Total Quantity' : 'Orders', sortable: true },
    { key: 'products', label: hasProductRows ? 'Unique Products' : 'Products', sortable: true },
    { key: 'quotation_count', label: 'Quotations', sortable: true },
    { key: 'enquiry_count', label: 'Enquiries', sortable: true },
    { key: 'target', label: 'Target', sortable: true },
    { key: 'target_year', label: 'Target Year', sortable: true },
    { key: 'target_achievement', label: 'Target %', sortable: true },
    { key: 'total_amount', label: hasProductRows ? 'Total Amount' : 'Target Achieved', sortable: true },
    { key: 'revenue_share', label: 'Revenue %', sortable: true },
    { key: 'email', label: 'Email', sortable: false },
    { key: 'phone', label: 'Phone', sortable: false },
    { key: 'action', label: 'Actions', sortable: false },
  ];
  let allColumnDefs = hasProductRows
    ? baseColumnDefs.filter(
        (col) =>
          col.key !== 'target' &&
          col.key !== 'target_year' &&
          col.key !== 'target_achievement' &&
          col.key !== 'revenue_share',
      )
    : baseColumnDefs;

  if (isProspectOnly) {
    allColumnDefs = allColumnDefs.filter(
      (col) =>
        col.key !== 'orders' &&
        col.key !== 'products' &&
        col.key !== 'target_achievement' &&
        col.key !== 'total_amount' &&
        col.key !== 'revenue_share',
    );
  }
  const show: Record<ColKey, boolean> = {
    type: true,
    name: true,
    address: false,
    city: true,
    state: true,
    zip: false,
    country: true,
    staff: false,
    orders: false,
    products: false,
    quotation_count: false,
    enquiry_count: false,
    target: false,
    target_year: false,
    target_achievement: false,
    total_amount: false,
    revenue_share: false,
    email: false,
    phone: false,
    action: false,
  };
  const addClient = () => {
    show.address = true;
    show.zip = true;
    show.staff = true;
    show.orders = true;
    show.products = true;
    show.target = true;
    show.target_year = true;
    show.target_achievement = true;
    show.total_amount = true;
    show.revenue_share = true;
    show.action = true;
  };
  const addProspect = () => {
    show.address = true;
    show.zip = true;
    show.staff = true;
    show.orders = false;
    show.products = false;
    show.quotation_count = true;
    show.enquiry_count = true;
    show.target = true;
    show.target_year = true;
    show.target_achievement = false;
    show.total_amount = false;
    show.revenue_share = false;
    show.action = true;
  };
  const addVendor = () => {
    show.address = true;
    show.zip = true;
    show.email = true;
    show.phone = true;
    show.action = true;
  };
  const addWarehouse = () => { /* base columns only */ };
  const addProduct = () => {
    show.city = true;
    show.state = true;
    show.country = true;
    show.orders = true;
    show.products = true;
    show.total_amount = true;
  };
  presentTypes.forEach((type) => {
    if (type === 'client') addClient();
    else if (type === 'prospect') addProspect();
    else if (type === 'vendor') addVendor();
    else if (type === 'warehouse') addWarehouse();
    else if (type === 'product') addProduct();
  });

  if (opts.columnVisibility) {
    Object.entries(opts.columnVisibility).forEach(([key, value]) => {
      if (Object.prototype.hasOwnProperty.call(show, key) && value !== undefined) {
        show[key as ColKey] = Boolean(value);
      }
    });
  }

  show.type = true;
  show.name = true;

  const columnVisibilityState: Record<ColKey, boolean> = {} as Record<ColKey, boolean>;
  allColumnDefs.forEach((col) => {
    columnVisibilityState[col.key] = Boolean(show[col.key]);
  });

  const columns = allColumnDefs.filter((col) => columnVisibilityState[col.key]);

  const lockedColumns = new Set<ColKey>(['type', 'name']);
  const columnCheckboxEntries = allColumnDefs
    .map((col) => {
      const isLocked = lockedColumns.has(col.key);
      const isChecked = columnVisibilityState[col.key];
      const disabledAttr = isLocked ? 'disabled' : '';
      const checkedAttr = isChecked ? 'checked' : '';
      const lockTitle = isLocked ? ' title="Required column"' : '';
      return `<label style="display:flex; align-items:center; gap:8px; font-size:13px; margin-bottom:6px;">
        <input type="checkbox" class="popup-table-column-checkbox" data-column-key="${col.key}" ${checkedAttr} ${disabledAttr}/>
        <span${lockTitle}>${col.label}</span>
      </label>`;
    })
    .join('');

  const columnToggleMarkup = showColumnToggle
    ? `<div class="popup-table-column-container" style="position:relative;">
         <button id="popup-table-column-toggle" type="button" class="popup-table-column-toggle" style="padding:8px 12px; border:1px solid #dadce0; border-radius:6px; background:#f8f9fa; cursor:pointer; font-size:13px; display:flex; align-items:center; gap:6px;">
           Columns
           <span style="font-size:10px;">&#9662;</span>
         </button>
         <div id="popup-table-column-menu" class="popup-table-column-menu" data-open="false" style="position:absolute; right:0; top:calc(100% + 6px); background:#ffffff; border:1px solid #dadce0; border-radius:8px; padding:10px 12px; box-shadow:0 12px 28px rgba(15,23,42,0.18); display:none; min-width:200px; max-height:260px; overflow-y:auto; z-index:100;">
           ${columnCheckboxEntries}
         </div>
       </div>`
    : '';

  const header = `
    <div class="popup-table-header" style="display:flex; flex-wrap:wrap; gap:12px; align-items:center; justify-content:space-between; padding:10px 10px 0 10px;">
      <div style="font-weight:600; color:#202124;">Results (${total})</div>
      <div style="position:relative; flex:1; min-width:220px;">
        <input id="popup-table-search" class="popup-table-search" value="${
          (opts.search || '').replace(/"/g, '&quot;')
        }" placeholder="Search..." style="width:100%; padding:8px 28px 8px 10px; border:1px solid #dadce0; border-radius:6px; font-size:14px;" />
        <span id="popup-table-search-spinner" style="position:absolute; right:8px; top:36%; transform:translateY(-50%); display:none; width:16px; height:16px; border:2px solid #1a73e8; border-top-color: transparent; border-radius:50%; animation: spin 0.8s linear infinite;"></span>
      </div>
      ${columnToggleMarkup}
      <select id="popup-table-page-size" class="popup-table-page-size" style="padding:8px 10px; border:1px solid #dadce0; border-radius:6px; font-size:14px;">
        ${[5,10,20,50].map(size => `<option value="${size}" ${size===pageSize?'selected':''}>${size}/page</option>`).join('')}
      </select>
    </div>
  `;

  const bodyRows = pageRows
    .map((row) => {
      const linkColor =
        row.type === 'client'
          ? 'rgb(0, 145, 5)'
          : row.type === 'vendor'
          ? '#fbbc04'
          : row.type === 'prospect'
          ? '#1a73e8'
          : row.type === 'product'
          ? '#1a73e8'
          : '#ea4335';
      const nameCell = row.userid
        ? `<a href="#" onclick="handleEntityLinkClick(event, '${row.userid}')" style="color:${linkColor}; text-decoration: underline; cursor: pointer;">${row.name || ''}</a>`
        : `${row.name || ''}`;
      const formatNumericDisplay = (value: unknown) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) {
          const compact = formatNumber(numeric);
          const exact = formatExactNumber(numeric);
          if (compact === exact) {
            return exact;
          }
          return `<span title="${exact}">${compact}</span>`;
        }
        return value !== undefined && value !== null ? String(value) : '';
      };
      const ordersCell =
        row.type === 'client' && row.userid
          ? `<span class="popup-stat orders" data-client-id="${row.userid}" style="color:#1a73e8; cursor:pointer;" onclick="handleEntityLinkClick(event, '${row.userid}')">${row.orders ?? ''}</span>`
          : row.type === 'product'
          ? formatNumericDisplay(row.orders)
          : `${row.orders ?? ''}`;
      const productsCell =
        row.type === 'client' && row.userid
          ? `<span class="popup-stat products" data-client-id="${row.userid}" style="color:#1a73e8; cursor:pointer;" onclick="handleEntityLinkClick(event, '${row.userid}')">${row.products ?? ''}</span>`
          : row.type === 'product'
          ? formatNumericDisplay(row.products)
          : `${row.products ?? ''}`;

      const actionBtn = (row.type === 'client' || row.type === 'vendor' || row.type === 'prospect')
        ? `<button class="edit-location-btn"
              style="background: ${row.type === 'client' ? 'rgb(0, 145, 5)' : (row.type === 'vendor' ? '#fbbc04' : '#1a73e8')}; color: white; border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 12px;"
              data-location-id="${row.location_id || ''}"
              data-user-id="${row.userid || ''}"
              data-company="${(row.name || '').replace(/"/g, '&quot;')}"
              data-address="${(row.address || '').replace(/"/g, '&quot;')}"
              data-city="${(row.city || '').replace(/"/g, '&quot;')}"
              data-state="${(row.state || '').replace(/"/g, '&quot;')}"
              data-country="${(row.country || '').replace(/"/g, '&quot;')}"
              data-zip="${(row.zip || '').replace(/"/g, '&quot;')}"
              data-website-validation-notes="${(row.website_validation_notes || '').replace(/"/g, '&quot;')}"
              data-website-found-address="${(row.website_found_address || '').replace(/"/g, '&quot;')}"
              data-website-found-city="${(row.website_found_city || '').replace(/"/g, '&quot;')}"
              data-website-found-state="${(row.website_found_state || '').replace(/"/g, '&quot;')}"
              data-website-found-country="${(row.website_found_country || '').replace(/"/g, '&quot;')}" 
            >Edit</button>`
        : '';
      const renderCell = (key: ColKey) => {
        switch (key) {
          case 'type': return `${row.type}`;
          case 'name': return nameCell;
          case 'address': return `${row.address || ''}`;
          case 'city': return `${row.city || ''}`;
          case 'state': return `${row.state || ''}`;
          case 'zip': return `${row.zip || ''}`;
          case 'country': return `${row.country || ''}`;
          case 'staff': return `${row.staff || ''}`;
          case 'orders': return `${ordersCell}`;
          case 'products': return `${productsCell}`;
          case 'target': return `${toAmount(row.target) || ''}`;
          case 'target_year': return `${row.target_year ?? ''}`;
          case 'target_achievement': return `${toPercent(row.target_achievement) || ''}`;
          case 'total_amount': return `${toAmount(row.total_amount) || ''}`;
          case 'revenue_share': {
            if (row.type !== 'client') return '';
            const amount = parseAmount(row.total_amount);
            if (amount <= 0 || totalRevenueAmount <= 0) return '';
            const share = (amount / totalRevenueAmount) * 100;
            const display = `${share.toFixed(2)}%`;
            return formatPercentCell(display, share);
          }
          case 'email': {
            const email = row.email || '';
            return formatMaskedHtml(email, maskEmail(email));
          }
          case 'phone': {
            const phone = row.phone || '';
            return formatMaskedHtml(phone, maskPhone(phone));
          }
          case 'action': return `${actionBtn}`;
          default: return '';
        }
      };

      return `
        <tr>
          ${columns.map(c => {
            const alignRight = c.key === 'orders' || c.key === 'products' || c.key === 'target' || c.key === 'target_year' || c.key === 'target_achievement' || c.key === 'total_amount' || c.key === 'revenue_share';
            const extra = c.key === 'type' ? ' text-transform:capitalize;' : '';
            return `<td style=\"padding:6px 8px; border-top:1px solid #e9ecef;${extra} ${alignRight ? 'text-align:right;' : ''}\">${renderCell(c.key)}</td>`;
          }).join('')}
        </tr>
      `;
    })
    .join('');

  const table = `
    <div class="popup-table-wrapper" style="padding:12px; width:100%;">
      ${header}
      <div style="margin-top:10px; overflow-x: scroll;height: calc(100vh - 330px);">
        <table style="border-collapse:collapse; width:100%; font-size:14px; border:1px solid #e9ecef;">
          <thead style="position:sticky; top:0; background:#f8f0fa; z-index:1;">
            <tr>
              ${columns.map(c => {
                const isActive = sortKey === (c.key as any);
                const arrowChar = !c.sortable ? '' : (isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅');
                const arrowStyle = !c.sortable ? '' : (isActive ? 'color:#1a73e8; opacity:1;' : 'color:#5f6368; opacity:0.6;');
                const content = c.sortable
                  ? `<button class=\"popup-table-sort\" data-sort-key=\"${c.key}\" style=\"background:none;border:none;padding:0;margin:0;color:#202124;cursor:pointer;font-weight:600;display:inline-flex;align-items:center;gap:4px;\">`+
                      `<span>${c.label}</span><span style=\"font-size:12px; ${arrowStyle}\">${arrowChar}</span>`+
                    `</button>`
                  : `<span style=\"font-weight:600; color:#202124;\">${c.label}</span>`;
                return `<th style=\"text-align:left; padding:8px; border-bottom:2px solid #e9ecef; white-space:nowrap;\">${content}</th>`;
              }).join('')}
            </tr>
          </thead>
          <tbody>
            ${bodyRows || `<tr><td colspan="${columns.length}" style="padding:12px; text-align:center; color:#5f6368;">No results</td></tr>`}
          </tbody>
        </table>
      </div>
      <div class="popup-table-footer" style="display:flex; align-items:center; justify-content:space-between; padding:10px; color:#5f6368; font-size:12px;">
        <div>Page ${currentPage} of ${totalPages}</div>
        <div style="display:flex; align-items:center; gap:8px;">
          <button class="popup-table-page-btn" data-page="${Math.max(1, currentPage - 1)}" data-total-pages="${totalPages}" ${
            currentPage === 1 ? 'disabled' : ''
          } style="padding:8px 12px; border:1px solid #dadce0; background:#fff; border-radius:6px; cursor:pointer;">Prev</button>
          <button class="popup-table-page-btn" data-page="${Math.min(totalPages, currentPage + 1)}" data-total-pages="${totalPages}" ${
            currentPage === totalPages ? 'disabled' : ''
          } style="padding:8px 12px; border:1px solid #dadce0; background:#fff; border-radius:6px; cursor:pointer;">Next</button>
        </div>
      </div>
      <textarea id="popup-table-data" style="display:none;">${decodeURIComponent(encodedDataset)}</textarea>
      <textarea id="popup-table-state" style="display:none;">${JSON.stringify({ page: currentPage, pageSize, search: opts.search || '', sortKey: sortKey || '', sortDir, columnVisibility: columnVisibilityState })}</textarea>
    </div>
  `;
  return table;
};

export const createPopupContent = async (data: any, type: string) => {
  switch (type) {
    case 'client':
      // Get userid for the onclick handler
      const clientUserId = data.userid || data.properties?.userid;

      return `
        <div class="popup-header" style="background: #e6f4ea; border-bottom-color: rgb(0, 145, 5);">
            <h3 style="color: rgb(0, 145, 5);">
                <i class="fa fa-building"></i>
                <a href="#" onclick="handleEntityLinkClick(event, '${clientUserId}')" style="color: rgb(0, 145, 5); text-decoration: underline; cursor: pointer;">${data.company}</a>
            </h3>
        </div>
        <div class="popup-content">
            <div class="popup-section">
                <div class="popup-section-title" style="color: #333; display: flex; align-items: center; gap: 8px;">
                    <i class="fa fa-map-marker" aria-hidden="true"></i> Location
                    ${data.address_matched !== undefined ? `
                        <span title="${data.address_matched === 1 ? 'Address matched' : 'Address not matched'}" style="cursor: help;">
                            ${data.address_matched === 1 ? 
                                '<i class="fa fa-check-circle" style="color: #52c41a; font-size: 16px;"></i>' : 
                                '<i class="fa fa-times-circle" style="color: #ff4d4f; font-size: 16px;"></i>'
                            }
                        </span>
                    ` : ''}
                </div>
                <div class="popup-address">
                    <div>${data.address}</div>
                    <div>${data.city}, ${data.state} ${data.zip}</div>
                    <div style="color: #5f6368;">${data.country ? data.country.short_name : ''}</div>
                </div>
            </div>
            ${data.matching_products && data.matching_products.length > 0 ? `
                <div class="popup-section">
                    <div class="popup-section-title" style="color: #333;">
                        <i class="fa fa-box"></i> Matching Products
                    </div>
                    <div class="popup-address">
                        ${data.matching_products.map((product: string) => `<div>${product}</div>`).join('')}
                    </div>
                </div>
            ` : ''}
            <div class="popup-section">
                <div class="popup-section-title" style="color: #333;">
                    <i class="fa fa-user"></i> Sales Rep
                </div>
                <div class="popup-address">
                    <div>${data.staff_name || 'N/A'}</div>
                </div>
            </div>
            <div class="popup-section">
                <div class="popup-section-title" style="color: #333;">
                    <i class="fa fa-chart-line"></i> Sales Overview
                </div>
                <div class="popup-stats">
                    <div class="popup-stat orders" style="background: #e6f4ea; border-radius: 4px; padding: 8px; text-align: center; cursor: pointer;" data-client-id="${data.userid || data.properties?.userid}" onclick="handleEntityLinkClick(event, '${data.userid || data.properties?.userid}')">
                        <strong style="color: rgb(0, 145, 5);">${data.order_count || 0}</strong>
                        <span style="color: #1a73e8;">Orders</span>
                    </div>
                    <div class="popup-stat products" style="background: #e8f0fe; border-radius: 4px; padding: 8px; text-align: center; cursor: pointer;" data-client-id="${data.userid || data.properties?.userid}" onclick="handleEntityLinkClick(event, '${data.userid || data.properties?.userid}')">
                        <strong style="color: #1a73e8;">${data.unique_products || 0}</strong>
                        <span style="color: #1a73e8;">Products</span>
                    </div>
                    <div class="popup-stat amount" style="background: #e8f0fe; border-radius: 4px; padding: 8px; text-align: center;">
                        
                        <button class="balance-btn" 
                                style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; padding: 12px 20px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 600; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4); transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 44px;"
                                data-action="fetch-balance"
                                data-partner-id="${data.userid || data.properties?.userid}"
                                onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.6)'"
                                onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 15px rgba(102, 126, 234, 0.4)'">
                            Closing Balance
                        </button>
                        <div class="balance-amount" style="display: none;">
                            <strong style="color: #ea4335; font-size: 18px;">$0.00</strong>
                        </div>
                    </div>
                </div>
            </div>
            <div class="popup-section">
                <div class="popup-actions" style="display: flex; justify-content: center; margin-top: 12px;">
                    <button class="edit-location-btn" 
                            style="background: #1a73e8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; display: flex; align-items: center; gap: 6px;"
                            data-location-id="${data.id || ''}"
                            data-user-id="${data.userid || data.properties?.userid}"
                            data-company="${data.company || ''}"
                            data-address="${data.address || ''}"
                            data-city="${data.city || ''}"
                            data-state="${data.state || ''}"
                            data-country="${data.country ? data.country.short_name : ''}"
                            data-zip="${data.zip || ''}"
                            data-website-validation-notes="${data.website_validation_notes || ''}"
                            data-website-found-address="${data.website_found_address || ''}"
                            data-website-found-city="${data.website_found_city || ''}"
                            data-website-found-state="${data.website_found_state || ''}"
                            data-website-found-country="${data.website_found_country || ''}">
                        <i class="fa fa-edit"></i> Edit Location
                    </button>
                </div>
            </div>
        </div>`;

    case 'prospect':
      // Get userid for the onclick handler
      const prospectUserId = data.userid || data.properties?.userid;

      return `
        <div class="popup-header" style="background: #e8f0fe; border-bottom-color: #1a73e8;">
            <h3 style="color: #1a73e8;">
                <i class="fa fa-user-tie"></i>
                <a href="#" onclick="handleEntityLinkClick(event, '${prospectUserId}')" style="color: #1a73e8; text-decoration: underline; cursor: pointer;">${data.company}</a>
            </h3>
        </div>
        <div class="popup-content">
            <div class="popup-section">
                <div class="popup-section-title">
                    <i class="fa fa-map-marker" aria-hidden="true"></i> Location
                </div>
                <div class="popup-address">
                    <div>${data.city}${data.state ? `, ${data.state}` : ''} ${data.zip || ''}</div>
                    <div style="color: #5f6368;">${data.country ? data.country.short_name : 'N/A'}</div>
                </div>
            </div>
            <div class="popup-section">
                <div class="popup-section-title">
                    <i class="fa fa-user"></i> Sales Rep
                </div>
                <div class="popup-address">
                    <div>${data.staff_name || 'N/A'}</div>
                </div>
            </div>
        </div>`;

    case 'vendor':
      // Get userid for the onclick handler
      const vendorUserId = data.userid || data.properties?.userid;

      return `
        <div class="popup-header" style="background: #fff7e6; border-bottom-color: #fbbc04;">
            <h3 style="color: #fbbc04;">
                <i class="fa fa-truck"></i>
                <a href="#" onclick="handleEntityLinkClick(event, '${vendorUserId}')" style="color: #fbbc04; text-decoration: underline; cursor: pointer;">${data.company}</a>
            </h3>
        </div>
        <div class="popup-content">
            <div class="popup-section">
                <div class="popup-section-title">
                    <i class="fa fa-map-marker" aria-hidden="true"></i> Location
                </div>
                <div class="popup-address">
                    <div>${data.address || 'N/A'}</div>
                    <div>${data.city}${data.state ? `, ${data.state}` : ''} ${data.zip || ''}</div>
                    <div style="color: #5f6368;">${data.country ? data.country.short_name : 'N/A'}</div>
                </div>
            </div>
            <div class="popup-section">
                <div class="popup-section-title">
                    <i class="fa fa-info-circle"></i> Contact Information
                </div>
                <div class="popup-address">
                    <div>Email: ${
                      data.email_1
                        ? formatMaskedHtml(data.email_1, maskEmail(data.email_1))
                        : 'N/A'
                    }</div>
                    <div>Phone Number: ${
                      data.phonenumber
                        ? formatMaskedHtml(data.phonenumber, maskPhone(data.phonenumber))
                        : 'N/A'
                    }</div>
                </div>
            </div>
            <div class="popup-section">
                <div class="popup-actions" style="display: flex; justify-content: center; margin-top: 12px;">
                    <button class="edit-location-btn" 
                            style="background: #fbbc04; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; display: flex; align-items: center; gap: 6px;"
                            data-location-id="${data.id || ''}"
                            data-user-id="${data.userid || data.properties?.userid}"
                            data-company="${data.company || ''}"
                            data-address="${data.address || ''}"
                            data-city="${data.city || ''}"
                            data-state="${data.state || ''}"
                            data-country="${data.country ? data.country.short_name : ''}"
                            data-zip="${data.zip || ''}"
                            data-website-validation-notes="${data.website_validation_notes || ''}"
                            data-website-found-address="${data.website_found_address || ''}"
                            data-website-found-city="${data.website_found_city || ''}"
                            data-website-found-state="${data.website_found_state || ''}"
                            data-website-found-country="${data.website_found_country || ''}">
                        <i class="fa fa-edit"></i> Edit Location
                    </button>
                </div>
            </div>
        </div>`;

    case 'warehouse':
      return `
        <div class="popup-header" style="background: #fce8e6; border-bottom-color: #ea4335;">
            <h3 style="color: #ea4335;">
                <i class="fa fa-warehouse"></i>
                ${data.name}
            </h3>
        </div>
        <div class="popup-content">
            <div class="popup-section">
                <div class="popup-section-title">
                    <i class="fa fa-map-marker" aria-hidden="true"></i> Location
                </div>
                <div class="popup-address">
                    <div>${data.address ? data.address.replace(/\n/g, '<br>').replace(/(<br>){3,}/g, '<br><br>') : ''}</div>
                    <div>${data.city || ''}, ${data.state || ''} ${data.zip || ''}</div>
                    <div style="color: #ea4335; margin-top: 8px;">
                        <i class="fa fa-check-circle"></i> Active Warehouse
                    </div>
                </div>
            </div>
        </div>`;

    default:
      return '';
  }
};
